import React, { useEffect, useRef, useState } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import Cropper from "react-cropper";
import * as cn from "classnames/bind";
import * as ButtonStyles from "content/button/button.css";
import * as styles from "./image-cropper.css";
import { ApplicationState } from "store";
import { actionCreators } from "ui/store";
import { connect } from "react-redux";
import { ImageCropperOptions } from "burde";
import { CalendarSteps } from "ui/constants";

type ImageCropperProps = {
    imageCropperOptions: ImageCropperOptions;
    calendarSize: string;
} & typeof actionCreators &
    RouteComponentProps<{
        step?: string;
        sizeName?: string;
        location?: any;
        history?: any;
    }>;

const cx = cn.bind({ ...styles, ...ButtonStyles });

const modalBoxCn = (showModal: boolean) =>
    cx({
        modalBox: true,
        modalBoxShow: showModal,
    });

const menuListCn = () =>
    cx({
        menuList: true,
    });

const overlayCn = (showModal: boolean) =>
    cx({
        overlay: showModal,
    });

const spinnerWrapperCn = (isVisible: boolean) =>
    cx({
        spinnerWrapper: true,
        spinnerWrapperVisible: isVisible,
    });

const OwnerPageImageCropper: React.FC<ImageCropperProps> = ({
    imageCropperOptions,
    UpdateImageCropper,
    calendarSize,
    location,
}) => {
    let cropperRef = useRef<HTMLImageElement>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOwnerPage, setIsOwnerPage] = useState(false);

    useEffect(() => {
        if (location.pathname.endsWith(CalendarSteps.ownerPage)) {
            setIsOwnerPage(true);
        } else {
            setIsOwnerPage(false);
        }
    }, [location]);

    useEffect(() => {
        //Needed for EDGE
        require("blueimp-canvas-to-blob");
    }, []);

    const saveImage = async () => {
        if (cropperRef.current == null) {
            return;
        }

        setIsLoading(true);

        const imageElement: any = cropperRef.current;
        const cropper = imageElement.cropper;
        const croppedCanvas = cropper.getCroppedCanvas({
            fillColor: "#fff",
        });

        croppedCanvas.toBlob(
            async (blob: Blob | null) => await handleSaveBlob(blob),
        );
    };

    const handleSaveBlob = async (blob: Blob | null) => {
        if (blob === null) {
            return;
        }

        const { onSaveCallback } = imageCropperOptions;

        if (onSaveCallback === null) {
            return;
        }

        await onSaveCallback(blob, "A5"); // Send A5 since owner images are always the same size.

        setIsLoading(false);
        closeCropperWindow();
    };

    const rotateImage = (degrees: number) => {
        if (cropperRef.current == null) {
            return;
        }

        const imageElement: any = cropperRef.current;
        imageElement.cropper.rotate(degrees);
    };

    const zoomImage = (amount: number) => {
        if (cropperRef.current == null) {
            return;
        }

        const imageElement: any = cropperRef.current;
        imageElement.cropper.zoom(amount);
    };

    const closeCropperWindow = () => {
        UpdateImageCropper({
            isVisible: false,
            imageUrl: "",
            onSaveCallback: null,
        });
    };

    return (
        <React.Fragment>
            <div className={modalBoxCn(imageCropperOptions.isVisible)}>
                <div className={spinnerWrapperCn(isLoading)}>
                    <div
                        style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                        }}>
                        <img
                            src="images/spinner.svg"
                            className={styles.spinnerImage}
                        />
                        <div className={styles.spinnerOverlay} />
                    </div>
                </div>
                <Cropper
                    ref={cropperRef as any}
                    src={imageCropperOptions.imageUrl}
                    style={{ width: "100%", height: "70vh" }}
                    aspectRatio={1}
                    guides={false}
                    rotatable={true}
                    zoomOnWheel={true}
                    zoomOnTouch={true}
                    responsive={true}
                    cropend={() => {
                        const imageElement: any = cropperRef.current;
                        const cropper = imageElement.cropper;
                        const croppedImage = cropper.getCroppedCanvas(cropper);
                    }}
                />
                <div
                    style={{
                        background: "white",
                        borderRadius: "4px",
                        backgroundColor: "#f1f1f1",
                    }}>
                    <ul className={menuListCn()}>
                        <li
                            onClick={() => {
                                closeCropperWindow();
                            }}>
                            <span className="far fa-times-circle" />
                        </li>
                        <li
                            onClick={() => {
                                rotateImage(-90);
                            }}>
                            <span className="fas fa-undo" />
                        </li>
                        <li
                            onClick={() => {
                                rotateImage(90);
                            }}>
                            <span className="fas fa-undo fa-flip-horizontal" />
                        </li>
                        <li
                            onClick={() => {
                                zoomImage(0.1);
                            }}>
                            <span className="fas fa-search-plus" />
                        </li>
                        <li
                            onClick={() => {
                                zoomImage(-0.1);
                            }}>
                            <span className="fas fa-search-minus" />
                        </li>
                        <li
                            style={{ backgroundColor: "#cfebef" }}
                            onClick={saveImage}>
                            <span className="far fa-check-circle" />
                        </li>
                    </ul>
                </div>
            </div>
            <div className={overlayCn(imageCropperOptions.isVisible)} />
        </React.Fragment>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    imageCropperOptions: state.ui.ImageCropperOptions,
    calendarSize: state.calendar.calendarSize,
});

const mapDispatchToProps = {
    ...actionCreators,
};

const connectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps,
)(OwnerPageImageCropper);

export default withRouter(connectedComponent);
